import { useCallback, useState } from 'react';
import { ClientResponse } from '../../../model/ClientResponse';
import { ClientStatusError } from '../../../model/ClientStatusError';
import { useSecurityContext } from '../../SecurityContext';
import { ClientStatusSearchResults } from '../../../model/ClientStatusSearchResults';

interface ReturnType<T> {
  wrapClient(...args): Promise<ClientResponse<T>>;
  data: T;
  status: ClientStatusSearchResults;
}

type ClientCall<T> = (...args) => Promise<ClientResponse<T>>;

export default <T> (client: ClientCall<T>): ReturnType<T> => {
  const { accessToken, organizationId } = useSecurityContext();

  const [status, setStatus] = useState<ClientStatusSearchResults>({
    failed: false,
    finished: false,
    generalErrors: [],
    loading: false,
    responseCode: undefined,
  });
  const [data, setData] = useState<T>(null);

  const wrapClient = useCallback(
    async (...args) => {
      setStatus(prevState => ({ ...prevState, loading: true }));
      try {
        const headers = {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(organizationId && { OrganizationId: encodeURIComponent(organizationId) }),
        };
        const response = await client(...args, { headers });

        const newStatus = {
          responseCode: response.responseCode,
          loading: false,
          finished: true,
          generalErrors: [],
          failed: false,
        };

        setData(response.data);
        setStatus(newStatus);

        return Promise.resolve(response);
      } catch (e) {
        const generalResponseErrors: ClientStatusError[] = [];

        if (e.response == null) {
          const newStatus = {
            responseCode: 503,
            loading: false,
            finished: true,
            generalErrors: generalResponseErrors,
            failed: true,
          };

          setData(null);
          setStatus(newStatus);

          return Promise.reject();
        }

        const statusCode = e.response.status;
        let responseData = e.response.data;
        if (e.response.data instanceof Blob) {
          try {
            responseData = JSON.parse(await e.response.data.text());
            // eslint-disable-next-line no-empty
          } catch (ex) {
          }
        }

        responseData?.generalErrors?.forEach(error => generalResponseErrors.push(
          { message: error },
        ));

        const newStatus = {
          responseCode: statusCode,
          loading: false,
          finished: true,
          generalErrors: generalResponseErrors,
          failed: true,
        };

        setData(null);
        setStatus(newStatus);

        return Promise.reject();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, accessToken, organizationId],
  );

  return {
    wrapClient,
    data,
    status,
  };
};
